import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useQuery, useMutation, gql } from '@apollo/client';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import s from './Leadership.module.css';
import { PATHS } from '../../../paths';

import { GET_ME } from '../../../queries/getMe';
import { GET_SUPPORTED_COMMUNITIES } from '../../../queries/getSupportedCommunities';
import { GET_PRAYER_MEETINGS } from '../../../queries/getPrayerMeetings';
import { GET_JOYRIDES } from '../../../queries/getJoyride';
import { GET_EXPOSURE_TRIPS } from '../../../queries/getExposureTrips';
import { GET_VOLUNTEER_ROLES } from '../../../queries/getVolunteerRoles';
import { GET_REPORTED_CONTENT } from '../../../queries/getReportedContent';

import { CREATE_PRAYER_MEETING } from '../../../mutations/CREATE_PRAYER_MEETING';
import { UPDATE_PRAYER_MEETING } from '../../../mutations/UPDATE_PRAYER_MEETING';
import { ARCHIVE_PRAYER_MEETING } from '../../../mutations/ARCHIVE_PRAYER_MEETING';
import { SET_JOYRIDE, CREATE_JOYRIDE, ACTIVATE_JOYRIDE, ARCHIVE_JOYRIDE } from '../../../mutations/SET_JOYRIDE';
import { CREATE_EXPOSURE_TRIP } from '../../../mutations/CREATE_EXPOSURE_TRIP';
import { CREATE_LEADERSHIP_POST } from '../../../mutations/CREATE_LEADERSHIP_POST';
import { POST_TITLE_LIMIT, POST_DESCRIPTION_LIMIT, characterCountColour } from '../../../constants';
import { CREATE_IMAGE_UPLOAD } from '../../../mutations/CREATE_IMAGE_UPLOAD';
import { UPDATE_EXPOSURE_TRIP } from '../../../mutations/UPDATE_EXPOSURE_TRIP';
import { ARCHIVE_EXPOSURE_TRIP } from '../../../mutations/ARCHIVE_EXPOSURE_TRIP';
import { SET_CONTENT_REVIEW_STATUS } from '../../../mutations/SET_CONTENT_REVIEW_STATUS';
import { MediaTile } from '../../../components/MediaTile';
import { useCanAccessLeadership, useOrganisationName } from '../../../hooks/canAccessLeadership';
import { useCanViewPeople } from '../../../hooks/organisations';
import {
  FILE_INPUT_ACCEPT, MAX_VIDEO_DURATION_SECONDS, putContentTypeForFile,
  uploadContentTypeForFile, validateMediaFiles,
} from '../../../utils/media';

/**
 * Whether this person administers the organisation — the other half of the "is there anything
 * for you in Settings" question, alongside `useCanViewPeople`.
 *
 * Its own small document rather than a field on GET_ME: this page is the only caller, and it is
 * the server's own answer, so the button cannot promise something Settings will not show.
 */
const CAN_ADMINISTER_ROLES_GATE = gql`
  query LeadershipManageGate {
    canAdministerRoles
  }
`;

type Community = { supportedOrganisationId: number; scope: string; name: string; scopeLabel?: string | null };
type Interest = { id: number; expressedAt: string; person: { id: number; name: string } | null };
type Joyride = {
  id: number;
  name: string | null;
  startDate: string | null;
  endDate: string | null;
  challenge: string | null;
  target: number | null;
  challengeTeamUrl: string | null;
  archivedAt: string | null;
  isLive: boolean;
};
/** A volunteering opportunity as the supporter's leadership sees it — read-only here. */
type VolunteerOpportunity = {
  id: number;
  title: string | null;
  type: string | null;
  icon: string | null;
  interestedCount?: number;
  interested?: Interest[];
};
type PM = { id: number; name: string; location: string; time: string; frequency: string; interestedCount?: number; interested?: Interest[] };
type Trip = { id: number; name: string; date: string; location: string; durationDays: number; description: string; interestedCount?: number; interested?: Interest[] };

const EMPTY_PM: Omit<PM, 'id'> = { name: '', location: '', time: '', frequency: '' };
const EMPTY_TRIP: Omit<Trip, 'id'> = { name: '', date: '', location: '', durationDays: 0, description: '' };

function formatInterestDate(iso: string) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('default', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function formatTripDate(iso: string) {
  if (!iso) return { day: '—', month: '' };
  const d = new Date(iso);
  if (isNaN(d.getTime())) return { day: '—', month: '' };
  return {
    day: d.getUTCDate().toString(),
    month: d.toLocaleString('default', { month: 'short', timeZone: 'UTC' }).toUpperCase(),
  };
}

/**
 * Who has expressed interest in a trip or prayer meeting, for the leader's view.
 * Members express interest from the iOS app; this is the only place it surfaces to staff.
 */
function InterestReadout({ count, interested }: { count?: number; interested?: Interest[] }) {
  const total = count ?? 0;
  return (
    <div className={s.interestBlock}>
      <div className={s.interestHead}>
        <span className={s.interestCount}>{total}</span>
        <span>{total === 1 ? 'person interested' : 'people interested'}</span>
      </div>
      {interested && interested.length > 0 && (
        <ul className={s.interestList}>
          {interested.map(i => (
            <li key={i.id} className={s.interestItem}>
              <span className={s.interestName}>{i.person?.name ?? 'Unknown'}</span>
              <span className={s.interestDate}>{formatInterestDate(i.expressedAt)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Prayer Meetings ─────────────────────────────────────────────────────────

function PrayerMeetingsSection({ orgId }: { orgId: number }) {
  const { data, refetch } = useQuery(GET_PRAYER_MEETINGS, { variables: { supportedOrganisationId: orgId } });
  const [edits, setEdits] = useState<Record<number, PM>>({});
  const [newPM, setNewPM] = useState({ ...EMPTY_PM });
  const [adding, setAdding] = useState(false);

  const [create] = useMutation(CREATE_PRAYER_MEETING, { onCompleted: () => { setNewPM({ ...EMPTY_PM }); setAdding(false); refetch(); } });
  const [update] = useMutation(UPDATE_PRAYER_MEETING, { onCompleted: () => refetch() });
  const [archive] = useMutation(ARCHIVE_PRAYER_MEETING, { onCompleted: () => refetch() });

  const meetings: PM[] = data?.prayerMeetings ?? [];

  const patchEdit = (id: number, base: PM, patch: Partial<PM>) =>
    setEdits(prev => ({ ...prev, [id]: { ...base, ...prev[id], ...patch } }));

  const saveEdit = (id: number, row: PM) => {
    update({ variables: { ...row, supportedOrganisationId: orgId } });
    setEdits(prev => { const next = { ...prev }; delete next[id]; return next; });
  };

  return (
    <div className={s.section}>
      <div className={s.sectionHead}>
        <h2 className={s.sectionTitle}>Prayer Meetings</h2>
        <span className={s.sectionTag}>Community</span>
      </div>
      <div className={s.pmGrid}>
        {meetings.map(m => {
          const row = edits[m.id] ?? m;
          const isEditing = !!edits[m.id];
          return (
            <div key={m.id} className={`${s.pmCard} ${isEditing ? s.editing : ''}`}>
              {isEditing ? (
                <>
                  <input className={s.input} value={row.name} placeholder="Meeting name"
                    onChange={e => patchEdit(m.id, m, { name: e.target.value })} />
                  <div style={{ marginTop: '0.6rem', display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                    <input className={s.input} value={row.location ?? ''} placeholder="Location"
                      onChange={e => patchEdit(m.id, m, { location: e.target.value })} />
                    <input className={s.input} value={row.time ?? ''} placeholder="Time  e.g. Sundays 8am"
                      onChange={e => patchEdit(m.id, m, { time: e.target.value })} />
                    <input className={s.input} value={row.frequency ?? ''} placeholder="Frequency  e.g. Weekly"
                      onChange={e => patchEdit(m.id, m, { frequency: e.target.value })} />
                  </div>
                  <div className={s.pmActions}>
                    <button className={`${s.btn} ${s.btnPrimary} ${s.btnSm}`} onClick={() => saveEdit(m.id, row)}>Save</button>
                    <button className={`${s.btn} ${s.btnGhost} ${s.btnSm}`}
                      onClick={() => setEdits(prev => { const n = { ...prev }; delete n[m.id]; return n; })}>Cancel</button>
                    <button className={`${s.btn} ${s.btnDanger} ${s.btnSm}`}
                      onClick={() => archive({ variables: { id: m.id, supportedOrganisationId: orgId } })}>Delete</button>
                  </div>
                </>
              ) : (
                <>
                  <div className={s.pmName}>{m.name}</div>
                  <div className={s.pmMeta}>
                    {m.location && (
                      <div className={s.pmMetaRow}>
                        <span className={s.pmMetaIcon}>📍</span>
                        <span>{m.location}</span>
                      </div>
                    )}
                    {m.time && (
                      <div className={s.pmMetaRow}>
                        <span className={s.pmMetaIcon}>🕐</span>
                        <span>{m.time}</span>
                      </div>
                    )}
                    {m.frequency && <span className={s.freqBadge}>{m.frequency}</span>}
                  </div>
                  <div className={s.pmActions}>
                    <button className={`${s.btn} ${s.btnGhost} ${s.btnSm}`}
                      onClick={() => patchEdit(m.id, m, {})}>Edit</button>
                  </div>
                  <InterestReadout count={m.interestedCount} interested={m.interested} />
                </>
              )}
            </div>
          );
        })}

        {adding ? (
          <div className={`${s.pmCard} ${s.editing}`}>
            <input className={s.input} value={newPM.name} placeholder="Meeting name"
              onChange={e => setNewPM(p => ({ ...p, name: e.target.value }))} autoFocus />
            <div style={{ marginTop: '0.6rem', display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
              <input className={s.input} value={newPM.location} placeholder="Location"
                onChange={e => setNewPM(p => ({ ...p, location: e.target.value }))} />
              <input className={s.input} value={newPM.time} placeholder="Time  e.g. Sundays 8am"
                onChange={e => setNewPM(p => ({ ...p, time: e.target.value }))} />
              <input className={s.input} value={newPM.frequency} placeholder="Frequency  e.g. Weekly"
                onChange={e => setNewPM(p => ({ ...p, frequency: e.target.value }))} />
            </div>
            <div className={s.pmActions}>
              <button className={`${s.btn} ${s.btnPrimary} ${s.btnSm}`}
                onClick={() => create({ variables: { supportedOrganisationId: orgId, ...newPM } })}>Add</button>
              <button className={`${s.btn} ${s.btnGhost} ${s.btnSm}`} onClick={() => setAdding(false)}>Cancel</button>
            </div>
          </div>
        ) : (
          <button className={s.addCard} onClick={() => setAdding(true)}>
            <span className={s.addIcon}>+</span>
            <span>Add meeting</span>
          </button>
        )}

        {meetings.length === 0 && !adding && (
          <p className={s.empty}>No prayer meetings yet — add the first one.</p>
        )}
      </div>
    </div>
  );
}

// ── Joyride ─────────────────────────────────────────────────────────────────

function JoyrideSection() {
  const { data, refetch } = useQuery(GET_JOYRIDES);
  const campaigns: Joyride[] = data?.joyrides ?? [];
  const live = campaigns.find(c => c.isLive) ?? null;

  // Which campaign the form is editing. `null` means "composing a new one".
  const [editingId, setEditingId] = useState<number | null | undefined>(undefined);
  const isCreating = editingId === null;
  // Until the user picks something, the form follows the live campaign.
  const selected = isCreating ? null : campaigns.find(c => c.id === editingId) ?? live;

  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [challenge, setChallenge] = useState('');
  const [target, setTarget] = useState('');
  const [challengeTeamUrl, setChallengeTeamUrl] = useState('');
  const [saved, setSaved] = useState(false);
  const [saveKey, setSaveKey] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const fillFrom = (c: Joyride | null) => {
    setName(c?.name ?? '');
    setStartDate(c?.startDate ? new Date(c.startDate) : null);
    setEndDate(c?.endDate ? new Date(c.endDate) : null);
    setChallenge(c?.challenge ?? '');
    setTarget(c?.target != null ? String(c.target) : '');
    setChallengeTeamUrl(c?.challengeTeamUrl ?? '');
  };

  // Seed the editor whenever the selection changes (and on first load, from the live one).
  useEffect(() => {
    if (isCreating) return;
    fillFrom(selected ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id, isCreating]);

  const onDone = () => {
    refetch();
    setSaved(true);
    setSaveKey(k => k + 1);
    setTimeout(() => setSaved(false), 2100);
  };
  const onError = (e: any) => setError(e?.message ?? 'Something went wrong — please try again.');

  const [setJoyrideMutation] = useMutation(SET_JOYRIDE, { onCompleted: onDone, onError });
  const [createJoyrideMutation] = useMutation(CREATE_JOYRIDE, {
    onCompleted: (d: any) => { setEditingId(d?.createJoyride?.id ?? undefined); onDone(); },
    onError,
  });
  const [activateJoyride] = useMutation(ACTIVATE_JOYRIDE, { onCompleted: onDone, onError });
  const [archiveJoyride] = useMutation(ARCHIVE_JOYRIDE, { onCompleted: onDone, onError });

  const formValues = () => ({
    name: name || undefined,
    startDate: startDate ? startDate.toISOString() : undefined,
    endDate: endDate ? endDate.toISOString() : undefined,
    challenge: challenge || undefined,
    target: target ? parseInt(target, 10) : undefined,
    challengeTeamUrl: challengeTeamUrl || undefined,
  });

  const handleSave = () => {
    setError(null);
    if (isCreating) {
      createJoyrideMutation({ variables: formValues() });
    } else {
      // Fall back to the campaign's own values so clearing a field doesn't blank it.
      setJoyrideMutation({
        variables: {
          id: selected?.id,
          name: name || selected?.name || undefined,
          startDate: startDate ? startDate.toISOString() : (selected?.startDate ?? undefined),
          endDate: endDate ? endDate.toISOString() : (selected?.endDate ?? undefined),
          challenge: challenge || selected?.challenge || undefined,
          target: target ? parseInt(target, 10) : (selected?.target ?? undefined),
          challengeTeamUrl: challengeTeamUrl || selected?.challengeTeamUrl || undefined,
        },
      });
    }
  };

  const startNewCampaign = () => {
    setError(null);
    setEditingId(null);
    fillFrom(null);
  };

  // The campaign to copy when starting a new one — the live one if there is one, otherwise
  // the most recent archived. `campaigns` is already ordered live-first, newest-first.
  const previousCampaign = campaigns[0] ?? null;

  return (
    <div className={s.section}>
      <div className={s.sectionHead}>
        <h2 className={s.sectionTitle}>Joyride</h2>
        <span className={s.sectionTag}>Org-wide</span>
      </div>

      {error && (
        <div className={s.stubBanner}>
          <span className={s.stubBannerIcon}>⚠</span>
          <span>{error}</span>
        </div>
      )}

      <div className={s.joyrideBanner}>
        <div className={s.joyrideEditorHead}>
          <span className={s.joyrideEditorTitle}>
            {isCreating
              ? 'New campaign'
              : selected
                ? `Editing: ${selected.name || 'Untitled campaign'}${selected.isLive ? '' : ' (archived)'}`
                : 'New campaign'}
          </span>
          <div className={s.joyrideEditorActions}>
            {isCreating && previousCampaign && (
              <button className={`${s.btn} ${s.btnGhost} ${s.btnSm}`}
                onClick={() => fillFrom(previousCampaign)}>
                Prefill from last year&apos;s campaign
              </button>
            )}
            {!isCreating && (
              <button className={`${s.btn} ${s.btnGhost} ${s.btnSm}`} onClick={startNewCampaign}>
                + New campaign
              </button>
            )}
            {isCreating && campaigns.length > 0 && (
              <button className={`${s.btn} ${s.btnGhost} ${s.btnSm}`}
                onClick={() => { setEditingId(undefined); setError(null); }}>
                Cancel
              </button>
            )}
          </div>
        </div>

        <div className={s.joyrideFields}>
          <div className={s.field}>
            <label className={s.fieldLabel}>Campaign name</label>
            <input className={s.input} value={name} placeholder="e.g. Joyride 2026"
              onChange={e => setName(e.target.value)} />
          </div>
          <div className={s.field}>
            <label className={s.fieldLabel}>Start date</label>
            <DatePicker
              selected={startDate}
              onChange={d => setStartDate(d)}
              placeholderText="Start"
              dateFormat="d MMM yyyy"
              customInput={<input className={s.input} />}
              portalId="joyride-datepicker-portal"
            />
          </div>
          <div className={s.field}>
            <label className={s.fieldLabel}>End date</label>
            <DatePicker
              selected={endDate}
              onChange={d => setEndDate(d)}
              placeholderText="End"
              dateFormat="d MMM yyyy"
              customInput={<input className={s.input} />}
              portalId="joyride-datepicker-portal"
            />
          </div>
          <div className={s.field}>
            <label className={s.fieldLabel}>Challenge</label>
            <input className={s.input} value={challenge} placeholder="e.g. 300 km"
              onChange={e => setChallenge(e.target.value)} />
          </div>
          <div className={s.field}>
            <label className={s.fieldLabel}>Goal ($)</label>
            <input className={s.input} type="number" value={target} placeholder="50000"
              onChange={e => setTarget(e.target.value)} />
          </div>
          <div className={s.field}>
            <label className={s.fieldLabel}>Challenge team URL</label>
            <input className={s.input} type="url" value={challengeTeamUrl} placeholder="https://..."
              onChange={e => setChallengeTeamUrl(e.target.value)} />
          </div>
          <div className={s.field} style={{ justifyContent: 'flex-end' }}>
            <label className={s.fieldLabel}>&nbsp;</label>
            <button className={`${s.btn} ${s.btnPrimary}`} onClick={handleSave}>
              {isCreating ? 'Publish campaign' : 'Save campaign'}
            </button>
          </div>
        </div>

        {isCreating && live && (
          <div className={s.joyrideStatus}>
            <span className={s.joyrideStatusDot} />
            <span>Publishing this will archive “{live.name || 'the current campaign'}”.</span>
          </div>
        )}
        {saved && (
          <div key={saveKey} className={s.joyrideStatus}>
            <span className={s.savedPill}>✓ Saved</span>
          </div>
        )}
      </div>

      <div className={s.joyrideHistory}>
        <h3 className={s.joyrideHistoryTitle}>Campaigns</h3>
        {campaigns.length === 0 && (
          <p className={s.empty}>No campaigns yet — fill in the form above to publish your first.</p>
        )}
        {campaigns.map(c => (
          <div key={c.id} className={`${s.joyrideRow} ${c.isLive ? s.joyrideRowLive : ''}`}>
            <div className={s.joyrideRowMain}>
              <span className={s.joyrideRowName}>{c.name || 'Untitled campaign'}</span>
              <span className={c.isLive ? s.liveBadge : s.archivedBadge}>
                {c.isLive ? 'Live' : 'Archived'}
              </span>
              <span className={s.joyrideRowMeta}>
                {c.startDate && c.startDate.slice(0, 10)}
                {c.endDate && ` – ${c.endDate.slice(0, 10)}`}
                {c.challenge && ` · ${c.challenge}`}
                {c.target != null && ` · $${c.target.toLocaleString()} goal`}
              </span>
            </div>
            <div className={s.joyrideRowActions}>
              <button className={`${s.btn} ${s.btnGhost} ${s.btnSm}`}
                onClick={() => { setEditingId(c.id); setError(null); }}>Edit</button>
              {c.isLive ? (
                <button className={`${s.btn} ${s.btnGhost} ${s.btnSm}`}
                  onClick={() => archiveJoyride({ variables: { id: c.id } })}>Archive</button>
              ) : (
                <button className={`${s.btn} ${s.btnPrimary} ${s.btnSm}`}
                  onClick={() => activateJoyride({ variables: { id: c.id } })}>Make live</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Content review ──────────────────────────────────────────────────────────

type ReportedItem = {
  contentType: string;
  contentId: number;
  reviewStatus: string | null;
  reportCount: number;
  reportedAt: string | null;
  reasons: string[] | null;
  title: string | null;
  content: string | null;
  author: { id: number; name: string } | null;
  reporters: { id: number; name: string }[] | null;
};

/**
 * The queue of posts and comments members have reported. Approving restores the content
 * exactly as it was; removing hides it from the app for everyone.
 *
 * Org-wide rather than per-community, so there's no community selector here.
 */
function ContentReviewSection() {
  const [showDecided, setShowDecided] = useState(false);
  const { data, loading, refetch } = useQuery(GET_REPORTED_CONTENT, {
    variables: { onlyUnderReview: !showDecided },
    fetchPolicy: 'cache-and-network',
  });
  const [error, setError] = useState<string | null>(null);
  const [setStatus] = useMutation(SET_CONTENT_REVIEW_STATUS, {
    onCompleted: () => refetch(),
    onError: (e: any) => setError(e?.message ?? 'Something went wrong — please try again.'),
  });

  const items: ReportedItem[] = data?.reportedContent ?? [];

  const decide = (item: ReportedItem, reviewStatus: 'APPROVED' | 'REMOVED') => {
    setError(null);
    setStatus({ variables: { contentType: item.contentType, contentId: item.contentId, reviewStatus } });
  };

  return (
    <div className={s.section}>
      <div className={s.sectionHead}>
        <h2 className={s.sectionTitle}>Reported content</h2>
        <span className={s.sectionTag}>Org-wide</span>
      </div>

      {error && (
        <div className={s.stubBanner}>
          <span className={s.stubBannerIcon}>⚠</span>
          <span>{error}</span>
        </div>
      )}

      <div className={s.joyrideEditorHead}>
        <span className={s.joyrideEditorTitle}>
          {showDecided ? 'All reported content' : 'Awaiting review'}
        </span>
        <button className={`${s.btn} ${s.btnGhost} ${s.btnSm}`} onClick={() => setShowDecided(v => !v)}>
          {showDecided ? 'Show only awaiting review' : 'Show already decided'}
        </button>
      </div>

      {loading && items.length === 0 && <p className={s.empty}>Loading…</p>}
      {!loading && items.length === 0 && (
        <p className={s.empty}>
          {showDecided ? 'Nothing has been reported yet.' : 'Nothing is waiting for review. '}
        </p>
      )}

      {items.map(item => (
        <div key={`${item.contentType}:${item.contentId}`} className={s.joyrideRow}>
          <div className={s.joyrideRowMain}>
            <span className={item.reviewStatus === 'UNDER_REVIEW' ? s.archivedBadge : s.liveBadge}>
              {item.contentType === 'POST' ? 'Post' : 'Comment'}
            </span>
            {/* Reasons first — a moderator triages the queue by these before reading. */}
            {(item.reasons ?? []).map(reason => (
              <span key={reason} className={s.reasonBadge}>{reason}</span>
            ))}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', minWidth: 0 }}>
              {item.title && <span className={s.joyrideRowName}>{item.title}</span>}
              <span className={s.joyrideRowName} style={{ fontWeight: 400, whiteSpace: 'pre-wrap' }}>
                {item.content || <em>(no text)</em>}
              </span>
              <span className={s.joyrideRowMeta}>
                by {item.author?.name ?? 'Unknown'}
                {' · '}
                reported {item.reportCount === 1 ? 'once' : `${item.reportCount} times`}
                {item.reportedAt && ` · first ${formatInterestDate(item.reportedAt)}`}
                {item.reporters && item.reporters.length > 0 &&
                  ` · by ${item.reporters.map(r => r.name).join(', ')}`}
                {item.reviewStatus && item.reviewStatus !== 'UNDER_REVIEW' && ` · ${item.reviewStatus.toLowerCase()}`}
              </span>
            </div>
          </div>
          <div className={s.joyrideRowActions}>
            <button className={`${s.btn} ${s.btnGhost} ${s.btnSm}`} onClick={() => decide(item, 'APPROVED')}>
              Keep
            </button>
            <button className={`${s.btn} ${s.btnDanger} ${s.btnSm}`} onClick={() => decide(item, 'REMOVED')}>
              Remove
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Volunteering (read-only) ────────────────────────────────────────────────

/**
 * What a community is asking for help with, and who from this organisation has put their
 * hand up. Deliberately read-only: the opportunities belong to the community and are
 * authored by their admins on the Partnerships page.
 */
function VolunteeringSection({ orgId }: { orgId: number }) {
  const { data, loading } = useQuery(GET_VOLUNTEER_ROLES, { variables: { supportedOrganisationId: orgId } });
  const roles: VolunteerOpportunity[] = data?.volunteerRoles ?? [];

  return (
    <div className={s.section}>
      <div className={s.sectionHead}>
        <h2 className={s.sectionTitle}>Volunteering</h2>
        <span className={s.sectionTag}>Community</span>
      </div>

      {loading && roles.length === 0 && <p className={s.empty}>Loading opportunities…</p>}
      {!loading && roles.length === 0 && (
        <p className={s.empty}>
          This community hasn&apos;t listed any volunteering opportunities yet.
        </p>
      )}

      <div className={s.pmGrid}>
        {roles.map(role => (
          <div key={role.id} className={s.pmCard}>
            <div className={s.pmName}>{role.title || 'Untitled opportunity'}</div>
            {role.type && (
              <div className={s.pmMeta}>
                <span className={s.freqBadge}>{role.type}</span>
              </div>
            )}
            <InterestReadout count={role.interestedCount} interested={role.interested} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Community Visits ─────────────────────────────────────────────────────────

function VisitsSection({ orgId }: { orgId: number }) {
  const { data, refetch } = useQuery(GET_EXPOSURE_TRIPS, { variables: { supportedOrganisationId: orgId } });
  const [edits, setEdits] = useState<Record<number, Trip>>({});
  const [newTrip, setNewTrip] = useState({ ...EMPTY_TRIP });
  const [adding, setAdding] = useState(false);

  const [create] = useMutation(CREATE_EXPOSURE_TRIP, { onCompleted: () => { setNewTrip({ ...EMPTY_TRIP }); setAdding(false); refetch(); } });
  const [update] = useMutation(UPDATE_EXPOSURE_TRIP, { onCompleted: () => refetch() });
  const [archive] = useMutation(ARCHIVE_EXPOSURE_TRIP, { onCompleted: () => refetch() });

  const trips: Trip[] = data?.exposureTrips ?? [];

  const patchEdit = (id: number, base: Trip, patch: Partial<Trip>) =>
    setEdits(prev => ({ ...prev, [id]: { ...base, ...prev[id], ...patch } }));

  const saveEdit = (id: number, row: Trip) => {
    update({ variables: { ...row, supportedOrganisationId: orgId } });
    setEdits(prev => { const n = { ...prev }; delete n[id]; return n; });
  };

  return (
    <div className={s.section}>
      <div className={s.sectionHead}>
        <h2 className={s.sectionTitle}>Community Visits</h2>
        <span className={`${s.sectionTag} ${s.sectionTagJade}`}>Community</span>
      </div>
      <div className={s.visitList}>
        {trips.map(t => {
          const row = edits[t.id] ?? t;
          const isEditing = !!edits[t.id];
          const { day, month } = formatTripDate(t.date);

          if (isEditing) {
            return (
              <div key={t.id} className={`${s.visitCard} ${s.editing}`}>
                <div />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                  <div className={s.field}>
                    <label className={s.fieldLabel}>Name</label>
                    <input className={s.input} value={row.name ?? ''} placeholder="Trip name"
                      onChange={e => patchEdit(t.id, t, { name: e.target.value })} />
                  </div>
                  <div className={s.field}>
                    <label className={s.fieldLabel}>Date</label>
                    <DatePicker
                      selected={row.date ? new Date(row.date) : null}
                      onChange={d => patchEdit(t.id, t, { date: d ? d.toISOString() : '' })}
                      dateFormat="d MMM yyyy"
                      customInput={<input className={s.input} />}
                      placeholderText="Date"
                    />
                  </div>
                  <div className={s.field}>
                    <label className={s.fieldLabel}>Location</label>
                    <input className={s.input} value={row.location ?? ''} placeholder="Location"
                      onChange={e => patchEdit(t.id, t, { location: e.target.value })} />
                  </div>
                  <div className={s.field}>
                    <label className={s.fieldLabel}>Duration (days)</label>
                    <input className={s.input} type="number" value={row.durationDays || ''}
                      placeholder="10"
                      onChange={e => patchEdit(t.id, t, { durationDays: parseInt(e.target.value) || 0 })} />
                  </div>
                  <div className={s.field} style={{ gridColumn: '1 / -1' }}>
                    <label className={s.fieldLabel}>Description</label>
                    <input className={s.input} value={row.description ?? ''} placeholder="Details about the trip"
                      onChange={e => patchEdit(t.id, t, { description: e.target.value })} />
                  </div>
                </div>
                <div className={s.visitActions}>
                  <button className={`${s.btn} ${s.btnPrimary} ${s.btnSm}`} onClick={() => saveEdit(t.id, row)}>Save</button>
                  <button className={`${s.btn} ${s.btnGhost} ${s.btnSm}`}
                    onClick={() => setEdits(prev => { const n = { ...prev }; delete n[t.id]; return n; })}>Cancel</button>
                  <button className={`${s.btn} ${s.btnDanger} ${s.btnSm}`}
                    onClick={() => archive({ variables: { id: t.id, supportedOrganisationId: orgId } })}>Archive</button>
                </div>
              </div>
            );
          }

          return (
            <div key={t.id} className={s.visitCard}>
              <div className={s.visitDateBlock}>
                <div className={s.visitDay}>{day}</div>
                <div className={s.visitMonth}>{month}</div>
              </div>
              <div className={s.visitInfo}>
                <div className={s.visitName}>{t.name || 'Untitled visit'}</div>
                <div className={s.visitMeta}>
                  {t.location && <span>{t.location}</span>}
                  {t.durationDays ? <>{t.location ? ' · ' : ''}<span className={s.durationBadge}>{t.durationDays} days</span></> : null}
                  {t.description && <div style={{ marginTop: '0.25rem', opacity: 0.7 }}>{t.description}</div>}
                </div>
              </div>
              <div className={s.visitActions}>
                <button className={`${s.btn} ${s.btnGhost} ${s.btnSm}`}
                  onClick={() => patchEdit(t.id, t, {})}>Edit</button>
              </div>

              <InterestReadout count={t.interestedCount} interested={t.interested} />
            </div>
          );
        })}

        {adding ? (
          <div className={`${s.visitCard} ${s.editing}`} style={{ gridTemplateColumns: '1fr' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
              <div className={s.field}>
                <label className={s.fieldLabel}>Name</label>
                <input className={s.input} value={newTrip.name} placeholder="Trip name" autoFocus
                  onChange={e => setNewTrip(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div className={s.field}>
                <label className={s.fieldLabel}>Date</label>
                <DatePicker
                  selected={newTrip.date ? new Date(newTrip.date) : null}
                  onChange={d => setNewTrip(p => ({ ...p, date: d ? d.toISOString() : '' }))}
                  dateFormat="d MMM yyyy"
                  customInput={<input className={s.input} />}
                  placeholderText="Date"
                />
              </div>
              <div className={s.field}>
                <label className={s.fieldLabel}>Location</label>
                <input className={s.input} value={newTrip.location} placeholder="e.g. Kolkata, West Bengal"
                  onChange={e => setNewTrip(p => ({ ...p, location: e.target.value }))} />
              </div>
              <div className={s.field}>
                <label className={s.fieldLabel}>Duration (days)</label>
                <input className={s.input} type="number" value={newTrip.durationDays || ''}
                  placeholder="10"
                  onChange={e => setNewTrip(p => ({ ...p, durationDays: parseInt(e.target.value) || 0 }))} />
              </div>
              <div className={s.field} style={{ gridColumn: '1 / -1' }}>
                <label className={s.fieldLabel}>Description</label>
                <input className={s.input} value={newTrip.description} placeholder="Details about the trip"
                  onChange={e => setNewTrip(p => ({ ...p, description: e.target.value }))} />
              </div>
            </div>
            <div className={s.pmActions} style={{ marginTop: '0.85rem' }}>
              <button className={`${s.btn} ${s.btnPrimary} ${s.btnSm}`}
                onClick={() => create({ variables: { supportedOrganisationId: orgId, ...newTrip } })}>Add visit</button>
              <button className={`${s.btn} ${s.btnGhost} ${s.btnSm}`} onClick={() => setAdding(false)}>Cancel</button>
            </div>
          </div>
        ) : (
          <button className={s.addCard} style={{ minHeight: 64, flexDirection: 'row', justifyContent: 'flex-start', padding: '1rem 1.25rem' }}
            onClick={() => setAdding(true)}>
            <span className={s.addIcon} style={{ fontSize: '1.1rem' }}>+</span>
            <span>Add community visit</span>
          </button>
        )}

        {trips.length === 0 && !adding && (
          <p className={s.empty}>No visits scheduled — add the first one.</p>
        )}
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function communityKey(c: Community) {
  return `${c.supportedOrganisationId}:${c.scope}`;
}

function parseCommunityKey(key: string): { supportedOrganisationId: number; scope: string } | null {
  const idx = key.indexOf(':');
  if (idx === -1) return null;
  return { supportedOrganisationId: Number(key.slice(0, idx)), scope: key.slice(idx + 1) };
}

function communityLabel(c: Community) {
  return c.scope === '*' || !c.scopeLabel ? c.name : `${c.name} (${c.scopeLabel})`;
}

// ── Leadership Updates / Prayer Requests ──────────────────────────────────────

const POST_TYPE = {
  update: 'LEADERSHIP_UPDATE',
  prayer: 'LEADERSHIP_PRAYER_REQUEST',
} as const;

type DraftKind = keyof typeof POST_TYPE;
type DraftMedia = { id: number; url: string; contentType?: string | null };
type Draft = { id: number; kind: DraftKind; title: string; description: string; media?: DraftMedia[] };

/** A picked file plus the duration probed while validating it, and its preview object URL. */
type StagedMedia = { file: File; durationSeconds?: number; previewUrl: string };

function UpdatesComposer() {
  const [kind, setKind] = useState<DraftKind>('update');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<StagedMedia[]>([]);
  const [uploading, setUploading] = useState(false);
  const [posted, setPosted] = useState<Draft[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [createPost, { loading }] = useMutation(CREATE_LEADERSHIP_POST);
  const [createImageUpload] = useMutation(CREATE_IMAGE_UPLOAD);

  // These posts are announcements from the organisation, so they carry its name — mirrors
  // topicDisplayName on the backend, including falling back to the "Leadership …" wording.
  const organisationName = useOrganisationName();
  const updateLabel = organisationName ? `${organisationName} Update` : 'Leadership Update';
  const prayerLabel = organisationName ? `${organisationName} Prayer Request` : 'Leadership Prayer Request';

  const isPrayer = kind === 'prayer';
  const busy = loading || uploading;
  // Backend requires both title and description (non-null args).
  const canPost = title.trim() !== '' && description.trim() !== '' && !busy
    && title.length <= POST_TITLE_LIMIT && description.length <= POST_DESCRIPTION_LIMIT;

  // Videos need a duration probe (plus size/format checks) before staging, so this is async.
  const addFiles = async (incoming: FileList | null) => {
    if (!incoming) return;
    const { accepted, errors } = await validateMediaFiles(Array.from(incoming));
    setError(errors.length > 0 ? errors.join(' ') : null);
    if (accepted.length === 0) return;
    setFiles(prev => [
      ...prev,
      ...accepted.map(({ file, durationSeconds }) => ({
        file,
        durationSeconds,
        previewUrl: URL.createObjectURL(file),
      })),
    ]);
  };

  const removeFile = (index: number) =>
    setFiles(prev => {
      const target = prev[index];
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((_, i) => i !== index);
    });

  // Upload each staged file to S3 via a presigned PUT and return the new image ids.
  // Order is preserved: the backend stores it as PostImage.position.
  const uploadStagedFiles = async (): Promise<number[]> => {
    const ids: number[] = [];
    for (const { file, durationSeconds } of files) {
      const { data } = await createImageUpload({
        variables: {
          fileName: file.name,
          contentType: uploadContentTypeForFile(file),
          durationSeconds,
        },
      });
      const upload = data?.createImageUpload;
      if (!upload) continue;
      const response = await fetch(upload.uploadUrl, {
        method: 'PUT',
        body: file,
        // This header sets the stored object's content type; see putContentTypeForFile.
        headers: { 'Content-Type': putContentTypeForFile(file) },
      });
      if (!response.ok) {
        throw new Error(`Upload of ${file.name} failed (${response.status}).`);
      }
      ids.push(upload.image.id);
    }
    return ids;
  };

  const post = async () => {
    if (!canPost) return;
    setError(null);
    try {
      setUploading(true);
      const imageIds = files.length > 0 ? await uploadStagedFiles() : [];
      setUploading(false);

      const { data } = await createPost({
        variables: {
          title: title.trim(),
          description: description.trim(),
          type: POST_TYPE[kind],
          imageIds,
        },
      });
      const created = data?.createLeadershipPost;
      setPosted(p => [
        {
          id: created?.id ?? Date.now(),
          kind,
          title: title.trim(),
          description: description.trim(),
          media: (created?.images ?? []).map((img: any) => ({
            id: img.id,
            url: img.url,
            contentType: img.contentType,
          })),
        },
        ...p,
      ]);
      setTitle('');
      setDescription('');
      setFiles(prev => {
        prev.forEach(staged => URL.revokeObjectURL(staged.previewUrl));
        return [];
      });
    } catch (e: any) {
      setError(e?.message ?? 'Something went wrong — please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={s.section}>
      <div className={s.sectionHead}>
        <h2 className={s.sectionTitle}>
          {organisationName ? `${organisationName} Updates & Prayer Requests` : 'Updates & Prayer Requests'}
        </h2>
        <span className={s.sectionTag}>Org-wide</span>
      </div>

      {error && (
        <div className={s.stubBanner}>
          <span className={s.stubBannerIcon}>⚠</span>
          <span>{error}</span>
        </div>
      )}

      <div className={s.composer}>
        <div className={s.segment}>
          <button
            className={`${s.segmentBtn} ${!isPrayer ? s.segmentBtnActive : ''}`}
            onClick={() => setKind('update')}
          >
            {updateLabel}
          </button>
          <button
            className={`${s.segmentBtn} ${isPrayer ? s.segmentBtnActive : ''}`}
            onClick={() => setKind('prayer')}
          >
            {prayerLabel}
          </button>
        </div>

        <div className={s.field}>
          <label className={s.fieldLabel}>Title</label>
          <input
            className={s.input}
            value={title}
            placeholder={isPrayer ? 'e.g. Pray for the upcoming outreach' : 'e.g. Term 2 highlights'}
            onChange={e => setTitle(e.target.value)}
          />
          <div style={{ textAlign: 'right', fontSize: '0.8em', color: characterCountColour(title.length, POST_TITLE_LIMIT) }}>
            {title.length} / {POST_TITLE_LIMIT}
          </div>
        </div>

        <div className={s.field}>
          <label className={s.fieldLabel}>{isPrayer ? 'Prayer request' : 'Update'}</label>
          <textarea
            className={s.textarea}
            value={description}
            placeholder={isPrayer ? 'Share what the community can pray for…' : 'Share an update with the community…'}
            onChange={e => setDescription(e.target.value)}
          />
          <div style={{ textAlign: 'right', fontSize: '0.8em', color: characterCountColour(description.length, POST_DESCRIPTION_LIMIT) }}>
            {description.length} / {POST_DESCRIPTION_LIMIT}
          </div>
        </div>

        <div className={s.field}>
          <label className={s.fieldLabel}>
            Photos &amp; videos
            <span className={s.fieldHint}> · videos up to {MAX_VIDEO_DURATION_SECONDS}s</span>
          </label>
          <div className={s.photoRow}>
            {files.map((staged, i) => (
              <div key={staged.previewUrl} className={s.photoThumb}>
                <MediaTile
                  url={staged.previewUrl}
                  contentType={staged.file.type}
                  alt={staged.file.name}
                />
                <button
                  type="button"
                  className={s.photoRemove}
                  onClick={() => removeFile(i)}
                  aria-label="Remove item"
                >
                  ×
                </button>
              </div>
            ))}
            <label className={s.photoAdd}>
              <span className={s.photoAddIcon}>+</span>
              <span>Add media</span>
              <input
                type="file"
                accept={FILE_INPUT_ACCEPT}
                multiple
                style={{ display: 'none' }}
                onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
              />
            </label>
          </div>
        </div>

        <div className={s.composerFooter}>
          <button className={`${s.btn} ${s.btnPrimary}`} onClick={post} disabled={!canPost}>
            {uploading ? 'Uploading…' : loading ? 'Posting…' : isPrayer ? 'Post prayer request' : 'Post update'}
          </button>
        </div>
      </div>

      {posted.map(d => (
        <div key={d.id} className={`${s.draftCard} ${d.kind === 'prayer' ? s.draftCardPrayer : ''}`}>
          <span className={`${s.draftKind} ${d.kind === 'prayer' ? s.draftKindPrayer : ''}`}>
            {d.kind === 'prayer' ? prayerLabel : updateLabel}
          </span>
          {d.title && <div className={s.draftTitle}>{d.title}</div>}
          {d.description && <div className={s.draftDescription}>{d.description}</div>}
          {d.media && d.media.length > 0 && (
            <div className={s.photoRow}>
              {d.media.map(item => (
                <div key={item.id} className={s.photoThumb}>
                  <MediaTile url={item.url} contentType={item.contentType} />
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {posted.length === 0 && (
        <p className={s.empty}>Nothing posted this session — posts you publish will appear here.</p>
      )}
    </div>
  );
}

// ── Community picker prompt (shown in community-specific tabs) ────────────────

function CommunityPrompt({
  what,
  communities,
  onSelect,
}: {
  what: string;
  communities: Community[];
  onSelect: (key: string) => void;
}) {
  return (
    <div className={s.promptWrap}>
      <div className={s.promptIcon}>↑</div>
      <div className={s.promptTitle}>Select a community to manage {what}</div>
      <div className={s.promptHint}>
        {communities.length > 0
          ? 'Pick one below — it sets the community in the top bar too.'
          : 'No communities are available yet.'}
      </div>
      {communities.length > 0 && (
        <div className={s.promptOptions}>
          {communities.map(c => (
            <button
              key={communityKey(c)}
              className={s.promptOption}
              onClick={() => onSelect(communityKey(c))}
            >
              {communityLabel(c)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Root ─────────────────────────────────────────────────────────────────────

export function Leadership() {
  const { loading: meLoading } = useQuery(GET_ME);
  // The hook, not a second copy of the rule. This page used to OR `isPartnerLeadership` with
  // `isPartnerMembershipOfficer` inline, which is how a `PARTNER_ADMIN` — who the server lets in —
  // was redirected away from a page they can use. Which roles carry
  // `AUTHOR_LEADERSHIP_CONTENT` is per-organisation data edited on the roles page, so any list of
  // role names on this side is a copy that goes stale without anyone noticing.
  const canAccessLeadership = useCanAccessLeadership();
  // Both server-answered, so the escape hatch never offers a destination that redirects.
  const canViewPeople = useCanViewPeople();
  const { data: manageGate } = useQuery(CAN_ADMINISTER_ROLES_GATE);
  const canAdministerRoles = manageGate?.canAdministerRoles === true;

  const { data: communitiesData } = useQuery(GET_SUPPORTED_COMMUNITIES);
  const communities: Community[] = communitiesData?.supportedCommunities ?? [];
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [tab, setTab] = useState<'joyride' | 'prayer' | 'visits' | 'volunteering' | 'reports' | 'updates'>('updates');

  // Auto-select once the data loads if the leader supports exactly one community.
  useEffect(() => {
    if (selectedKey === null && communities.length === 1) {
      setSelectedKey(communityKey(communities[0]));
    }
  }, [communities, selectedKey]);

  const selected = selectedKey ? parseCommunityKey(selectedKey) : null;

  // Leadership and membership officers only — everyone else goes back to classes.
  // Wait for GET_ME so a real leader isn't bounced during the initial load.
  if (!meLoading && !canAccessLeadership) {
    return <Navigate to={PATHS.CLASSES} replace />;
  }

  const communitySelector = (
    <select
      className={s.communitySelect}
      value={selectedKey ?? ''}
      onChange={e => setSelectedKey(e.target.value || null)}
    >
      <option value="">Select a community…</option>
      {communities.map(c => (
        <option key={communityKey(c)} value={communityKey(c)}>
          {communityLabel(c)}
        </option>
      ))}
    </select>
  );

  return (
    <div className={s.shell}>
      {/* Desktop top bar */}
      <header className={s.topBar}>
        <span className={s.topBarMark}>♦ Joyya Leadership</span>
        <span className={s.topBarSep} />
        <div className={s.topBarCommunityWrap}>
          {communities.length > 0
            ? communitySelector
            : <span className={s.topBarCommunityPlaceholder}>Loading communities…</span>}
        </div>
        {/* The way out of this shell. It renders its own chrome instead of the app nav, so
            without this somebody who lands here has no route to Settings or the People list —
            only Log out.

            Shown when **either** answer is yes, not just `canAdministerRoles`: a
            PARTNER_MEMBERSHIP_OFFICER holds PERSON: MANAGE without ORGANISATION: MANAGE, and
            they are precisely the person who needs the people list. One button is enough —
            Settings is where both live, and the main nav takes over once you leave, with its
            Leadership entry as the way back. */}
        {(canAdministerRoles || canViewPeople) && (
          <Link to={PATHS.ADMIN_SETTINGS} className={s.topBarManage}>Manage organisation</Link>
        )}
        <Link to={PATHS.LOGOUT} className={s.topBarLogout}>Log out</Link>
      </header>

      <div className={s.body}>
        {/* Mobile community selector */}
        <div className={`${s.mobileCommunityBar}`} style={{ display: 'none' }} id="mobile-community-bar">
          <div className={s.mobileCommunityLabel}>Community</div>
          {communitySelector}
        </div>

        {/* Tabs */}
        <div className={s.tabBar}>
          <button
            className={`${s.tab} ${tab === 'updates' ? s.tabActive : ''}`}
            onClick={() => setTab('updates')}
          >
            Updates &amp; Prayers
          </button>
          <button
            className={`${s.tab} ${tab === 'prayer' ? s.tabActive : ''}`}
            onClick={() => setTab('prayer')}
          >
            Prayer Meetings
          </button>
          <button
            className={`${s.tab} ${tab === 'visits' ? s.tabActive : ''}`}
            onClick={() => setTab('visits')}
          >
            Community Visits
          </button>
          <button
            className={`${s.tab} ${tab === 'joyride' ? s.tabActive : ''}`}
            onClick={() => setTab('joyride')}
          >
            Joyride
          </button>
          <button
            className={`${s.tab} ${tab === 'volunteering' ? s.tabActive : ''}`}
            onClick={() => setTab('volunteering')}
          >
            Volunteering
          </button>
          <button
            className={`${s.tab} ${tab === 'reports' ? s.tabActive : ''}`}
            onClick={() => setTab('reports')}
          >
            Reported
          </button>
        </div>

        {/* Joyride is org-wide — no community needed */}
        {tab === 'joyride' && <JoyrideSection />}

        {tab === 'prayer' && (
          selected != null
            ? <PrayerMeetingsSection orgId={selected.supportedOrganisationId} />
            : <CommunityPrompt what="prayer meetings" communities={communities} onSelect={setSelectedKey} />
        )}

        {tab === 'visits' && (
          selected != null
            ? <VisitsSection orgId={selected.supportedOrganisationId} />
            : <CommunityPrompt what="community visits" communities={communities} onSelect={setSelectedKey} />
        )}

        {tab === 'volunteering' && (
          selected != null
            ? <VolunteeringSection orgId={selected.supportedOrganisationId} />
            : <CommunityPrompt what="volunteering opportunities" communities={communities} onSelect={setSelectedKey} />
        )}

        {/* Reported content is org-wide — no community needed */}
        {tab === 'reports' && <ContentReviewSection />}

        {tab === 'updates' && <UpdatesComposer />}
      </div>

      {/* Mobile nav — only shows on small screens via CSS override below */}
      <style>{`
        @media (max-width: 768px) {
          #mobile-community-bar { display: block !important; }
        }
      `}</style>
    </div>
  );
}
